import groupBy from 'lodash/groupBy';
import sumBy from 'lodash/sumBy';

import { ANALYTICS_ALL_STRING } from 'src/constants';
import { isUnknownCurrency, prepareCurrencyLabel } from 'src/helpers/currency';
import { compareByAlphabet } from 'src/helpers/sorting';
import type {
  AbSectionsTransformed,
  AvailableCountriesTransformed,
  AvailableCurrenciesTransformed,
  GetAbSectionsResponse,
  GetAvailableCountriesResponse,
  GetAvailableCurrenciesResponse,
  GetOpenedGamesResponse,
  GetOpenedSectionsResponse,
  GetTopPlayersResponse,
  OpenedGamesTransformed,
  OpenedSectionsTransformed,
  TopPlayersTransformed,
} from 'src/types/analytics';

export const transformGetAvailableCountriesResponse: (
  res: GetAvailableCountriesResponse,
) => AvailableCountriesTransformed = (res) =>
  res.availableCountries
    .sort((a, b) => compareByAlphabet(a.countryFullName, b.countryFullName))
    .map(({ countryIsoCode, countryFullName }) => ({
      value: countryIsoCode,
      label: countryFullName,
    }));

export const transformGetAvailableCurrenciesResponse: (
  res: GetAvailableCurrenciesResponse,
) => AvailableCurrenciesTransformed = ({ availableCurrencies }) => {
  const isUnknown = availableCurrencies.some(isUnknownCurrency);
  const transformedCurrencies = availableCurrencies.map((value) => ({ value, label: prepareCurrencyLabel(value) }));

  if (availableCurrencies.length > 1 && !isUnknown) {
    return [{ value: ANALYTICS_ALL_STRING, label: 'All currencies' }].concat(transformedCurrencies);
  }

  return transformedCurrencies;
};

export const transformGetOpenedSectionsResponse: (res: GetOpenedSectionsResponse) => OpenedSectionsTransformed = (
  res,
) => res.values;

export const transformGetOpenedGamesResponse: (res: GetOpenedGamesResponse) => OpenedGamesTransformed = (res) =>
  res.values;

const prepareSections = (data: GetAbSectionsResponse['sectionsPopularity']['a']) =>
  Object.entries(
    groupBy(
      data.flatMap(({ sections }) => sections),
      ({ sectionName }) => sectionName,
    ),
  )
    .map(([sectionName, sections]) => ({
      sectionName,
      gameOpens: sumBy(sections, 'gameOpens'),
      opensPercent: sumBy(sections, 'opensPercent'),
    }))
    .sort((a, b) => b.opensPercent - a.opensPercent)
    .map(({ sectionName }) => sectionName);

const appendEmypySections = (data: GetAbSectionsResponse['sectionsPopularity']['a'], sectionNames: string[]) =>
  data.map((day) => {
    const sections = sectionNames.map((sectionName) => {
      const foundSection = day.sections.find((section) => section.sectionName === sectionName);

      if (foundSection) {
        return foundSection;
      }

      return {
        sectionName,
        gameOpens: 0,
        opensPercent: 0,
      };
    });

    return {
      ...day,
      sections,
    };
  });

export const transformGetAbSectionsResponse: (res: GetAbSectionsResponse) => AbSectionsTransformed = (res) => {
  const aSections = prepareSections(res.sectionsPopularity.a);
  const bSections = prepareSections(res.sectionsPopularity.b);

  return {
    ...res,
    sectionsPopularity: {
      a: appendEmypySections(res.sectionsPopularity.a, aSections),
      b: appendEmypySections(res.sectionsPopularity.b, bSections),
      aSections,
      bSections,
    },
  };
};

export const transformGetTopPlayersResponse: (res: GetTopPlayersResponse) => TopPlayersTransformed = (res) =>
  res.values;
